/* eslint-disable no-unused-vars */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import { useDispatch } from 'react-redux';
import { monthlyBudgetAPI } from 'services/monthlyBudget.service';
// import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { uniqueField } from 'constants';

function addAditionalFields(arr) {
  return arr.map((item) => {
    return {
      ...item,
      isEdit: false,
      isBudgetPerGeo: item.countriesBudgetItems && item.countriesBudgetItems?.length > 0,
    };
  });
}

function removeAdditionalFields(arr) {
  const modifiedArray = arr.map((obj) => {
    const { isBudgetPerGeo, msgToUser, isEdit, ...newObj } = obj;
    return newObj;
  });
  return modifiedArray;
}

const putDefaultItems = (result) => {
  if (result.isSuccess) {
    return addAditionalFields(result.data);
  }
  return [];
};

const sortItems = (items, direction, key) => {
  if (!key) return items;

  return [...items].sort((a, b) => {
    if (key === 'renderedCountries' && a[key] === '–') return 1;
    if (direction === 'ascending') {
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return a[key].localeCompare(b[key]);
      }
      return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;
    } else if (direction === 'descending') {
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return b[key].localeCompare(a[key]);
      }
      return a[key] > b[key] ? -1 : a[key] < b[key] ? 1 : 0;
    }

    return 0;
  });
};

export const useFacade = (isOpen$, result, saveAction) => {
  // const indexErrorItems$ = useSignal([]);
  const virtuosoErrorMsgRef = useRef(null);
  const modalWindowIsEdited$ = useSignal(false);
  const isToggleBtnOn$ = useSignal(false);
  const sortConfig$ = useSignal(null);
  const userSearch$ = useSignal('');

  const [items, setItems] = useState(() => putDefaultItems(result));
  const itemsToDisplay$ = useSignal(items);

  const MIN_MONTHLY_BUDGET = 1000;

  useEffect(() => {
    if (result.status === 'fulfilled') {
      setItems(putDefaultItems(result));
      itemsToDisplay$.value = putDefaultItems(result);
    }
  }, [result.status]);

  const dispatch = useDispatch();

  const updateSortConfig = useCallback(
    (key) => {
      let direction = 'ascending';
      if (
        sortConfig$.value &&
        sortConfig$.value.key === key &&
        sortConfig$.value.direction === 'ascending'
      ) {
        direction = 'descending';
      }
      sortConfig$.value = { key, direction };
    },
    [sortConfig$.value],
  );

  const getSortDirection = useCallback(
    (key) => {
      if (!sortConfig$.value) return;
      return sortConfig$.value.key === key ? sortConfig$.value.direction : undefined;
    },
    [sortConfig$.value],
  );

  const saveBtnClick = useCallback(async () => {
    const errorArray = [];
    const res = items.map((item, ind) => {
      const id = item[uniqueField];
      const newitemInd = itemsToDisplay$.value.findIndex((el) => el[uniqueField] === id);
      const newitem = newitemInd !== -1 ? itemsToDisplay$.value[newitemInd] : null;

      if (newitem) {
        if (!newitem.isBudgetPerGeo && newitem.countriesBudgetItems?.length) {
          newitem.countriesBudgetItems = [];
        }
        if (
          // FOR GEOBUDGET
          // (newitem.monthlyBudget < MIN_MONTHLY_BUDGET && !newitem.isOpenBudget) ||
          // (newitem.isBudgetPerGeo && !newitem.countriesBudgetItems?.length)
          newitem.monthlyBudget &&
          newitem.monthlyBudget < MIN_MONTHLY_BUDGET
        ) {
          //console.log(newitem.monthlyBudget);
          errorArray.push(newitemInd);
        }
      }
      return newitem || item;
    });

    if (virtuosoErrorMsgRef.current && errorArray.length > 0) {
      virtuosoErrorMsgRef.current?.scrollToIndex({
        index: errorArray[0],
        align: 'start',
        behavior: 'smooth',
      });
      return;
    }
    const saveRes = await saveAction(removeAdditionalFields(res));
    dispatch(monthlyBudgetAPI.util.resetApiState());
    closeModal();
  }, [items, itemsToDisplay$.value?.length]);

  const updateGeoBudgetItems = useCallback(
    (pbaNumbersOrPackageName, array) => {
      modalWindowIsEdited$.value = true;
      itemsToDisplay$.value = itemsToDisplay$.value.map((item) => {
        if (pbaNumbersOrPackageName === item[uniqueField]) {
          item.countriesBudgetItems = array;
          item.monthlyBudget = array.reduce((acc, cur) => acc + +cur.budget, 0);
        }
        return item;
      });
    },
    [items, itemsToDisplay$.value],
  );

  const updateIsBudgetPerGeo = useCallback(
    (pbaNumbersOrPackageName, newValue) => {
      // modalWindowIsEdited$.value = true;
      itemsToDisplay$.value = itemsToDisplay$.value.map((item) => {
        if (pbaNumbersOrPackageName === item[uniqueField]) {
          item.isBudgetPerGeo = newValue;
          item.isEdit = false;
          item.monthlyBudget = 0;
          if (newValue) {
            item.isAgency = false;
            item.isOpenBudget = false;
          }
          if (newValue && item.countriesBudgetItems && item.countriesBudgetItems.length > 0) {
            item.monthlyBudget = item.countriesBudgetItems.reduce(
              (acc, cur) => acc + +cur.budget,
              0,
            );
          }
        }
        return item;
      });
    },
    [items],
  );

  const updateIsOpenBudget = useCallback(
    (pbaNumbersOrPackageName, newValue) => {
      modalWindowIsEdited$.value = true;
      itemsToDisplay$.value = itemsToDisplay$.value.map((item) => {
        if (pbaNumbersOrPackageName === item[uniqueField]) {
          item.isOpenBudget = newValue;
          item.isEdit = false;

          if (newValue) {
            item.monthlyBudget = 0;
            item.isAgency = false;
            item.isBudgetPerGeo = false;
          }
        }
        return item;
      });
    },
    [items],
  );

  const updateIsAgency = useCallback(
    (pbaNumbersOrPackageName, newValue) => {
      modalWindowIsEdited$.value = true;
      itemsToDisplay$.value = itemsToDisplay$.value.map((item) => {
        if (pbaNumbersOrPackageName === item[uniqueField]) {
          item.isAgency = newValue;
          item.isEdit = false;

          if (newValue) {
            item.monthlyBudget = 0;
            item.isOpenBudget = false;
            item.isBudgetPerGeo = false;
          }
        }
        return item;
      });
    },
    [items],
  );

  const changeSearchValue = useCallback((e) => {
    userSearch$.value = e.target.value;
  }, []);

  const handleDeleteSearch = () => {
    userSearch$.value = '';
  };

  const onChangeMonthlyBudget = useCallback(
    (pbaNumbersOrPackageName, newMonthlyBudget) => {
      modalWindowIsEdited$.value = true;
      itemsToDisplay$.value = itemsToDisplay$.value.map((item) => {
        if (item[uniqueField] === pbaNumbersOrPackageName) {
          item.monthlyBudget = newMonthlyBudget;
        }
        return item;
      });
    },
    [items],
  );

  const handleChangeIsEdit = useCallback(
    (pbaNumbersOrPackageName, newIsEdit) => {
      itemsToDisplay$.value = itemsToDisplay$.value.map((item) => {
        if (item[uniqueField] === pbaNumbersOrPackageName) {
          item.isEdit = newIsEdit;
        }
        return item;
      });
    },
    [items],
  );

  const closeModal = useCallback(() => {
    isOpen$.value = false;
  }, []);

  useEffect(() => {
    const filteredItems = items.filter((item) => {
      const matchesSearch = userSearch$.value
        ? item.pbaName.toLowerCase().includes(userSearch$.value.toLowerCase()) ||
          item.packageName.toLowerCase().includes(userSearch$.value.toLowerCase()) ||
          item.pbaNumbers.toLowerCase().includes(userSearch$.value.toLowerCase())
        : true;
      const matchesToggle = isToggleBtnOn$.value ? item.block && item.monthlyBudget > 0 : true;
      return matchesSearch && matchesToggle;
    });
    itemsToDisplay$.value = filteredItems;
  }, [userSearch$.value, isToggleBtnOn$.value]);

  useEffect(() => {
    itemsToDisplay$.value = sortItems(
      itemsToDisplay$.value,
      sortConfig$.value?.direction,
      sortConfig$.value?.key,
    );
  }, [sortConfig$.value?.direction, sortConfig$.value?.key]);

  return {
    userSearch$,
    changeSearchValue,
    isToggleBtnOn$,
    updateSortConfig,
    // indexErrorItems$,
    virtuosoErrorMsgRef,
    handleChangeIsEdit,
    itemsToDisplay$,
    onChangeMonthlyBudget,
    saveBtnClick,
    closeModal,
    modalWindowIsEdited$,
    getSortDirection,
    updateGeoBudgetItems,
    updateIsOpenBudget,
    updateIsAgency,
    updateIsBudgetPerGeo,
    handleDeleteSearch,
  };
};
